import React from 'react';
import {
  AdproductsUserBillingGetInvoicesApiHandler_scanInvoices,
  AdproductsUserBillingInvoiceApiHandler_getZuoraBillingFilterOptions,
  type AdproductsInvoiceCursorDto,
  type AdproductsZuoraBillingInvoiceDto,
  ZuoraInvoiceType,
} from '@zg-rentals/ts-adproducts-client';
import { LoadingConstants, useRentalManagerUser } from '@zg-rentals/rental-manager-env';
import { logError } from '@zg-rentals/log-error';

import { emitSetFilterEvent } from '../model/ClickstreamEvents';
import { localDateToDate } from '../utils/dateUtils';

export interface InvoiceFilterOption {
  label: string;
  value?: string;
  isChecked: boolean;
}

export interface InvoiceListFilterConfig {
  startDate?: string;
  endDate?: string;
  property: Array<InvoiceFilterOption>;
  city: Array<InvoiceFilterOption>;
  state: Array<InvoiceFilterOption>;
}

export interface InvoiceListState {
  invoices: Array<AdproductsZuoraBillingInvoiceDto>;
  cursor?: AdproductsInvoiceCursorDto;
  hasMoreData: boolean;
  errorMsg: string | null;
  loadingState: string;
  filters: InvoiceListFilterConfig;
  multiPaymentsEnabled: boolean;
}

const initFilters = (): InvoiceListFilterConfig => ({
  startDate: undefined,
  endDate: undefined,
  property: [],
  city: [],
  state: [],
});

export const initState = (): InvoiceListState => ({
  invoices: [],
  cursor: undefined,
  hasMoreData: false,
  errorMsg: null,
  loadingState: LoadingConstants.INITIAL,
  filters: initFilters(),
  multiPaymentsEnabled: false,
});

type InvoiceListContextType = {
  dueState: InvoiceListState;
  setDueState: React.Dispatch<React.SetStateAction<InvoiceListState>>;
  paidState: InvoiceListState;
  setPaidState: React.Dispatch<React.SetStateAction<InvoiceListState>>;
  clearAll: () => void;
};

export const InvoiceListContext = React.createContext<InvoiceListContextType | null>(null);

const getFilterItems = (filter: Array<InvoiceFilterOption>) =>
  filter.filter((option) => option.isChecked).map((option) => option.label);

const mapOptions = (options: Array<string>): Array<InvoiceFilterOption> => {
  if (!options) return [];
  return options
    .map((option) => ({
      label: option,
      isChecked: false,
    }))
    .sort();
};

const invoiceSort = (a: AdproductsZuoraBillingInvoiceDto, b: AdproductsZuoraBillingInvoiceDto) => {
  const dateDif: number = localDateToDate(b.invoiceLocalDate).valueOf() - localDateToDate(a.invoiceLocalDate).valueOf();
  if (dateDif !== 0) {
    return dateDif;
  }
  const propertyA = a.propertyNames && a.propertyNames[0] ? a.propertyNames[0] : '';
  const propertyB = b.propertyNames && b.propertyNames[0] ? b.propertyNames[0] : '';
  return propertyA.localeCompare(propertyB);
};

const applyClientFilters = (
  invoices: Array<AdproductsZuoraBillingInvoiceDto>,
  filters: InvoiceListFilterConfig,
  excludeNucleus: boolean,
): Array<AdproductsZuoraBillingInvoiceDto> => {
  const selectedCities = getFilterItems(filters.city);
  const selectedStates = getFilterItems(filters.state);
  const selectedAccounts = filters.property.filter((p) => p.isChecked).map((p) => p.value || '');

  return invoices.filter((invoice) => {
    if (excludeNucleus && invoice.invoiceType === ZuoraInvoiceType.NUCLEUS_LISTING) {
      return false;
    }
    if (selectedCities.length > 0 && !invoice.cities?.some((c) => selectedCities.includes(c))) {
      return false;
    }
    if (selectedStates.length > 0 && !invoice.states?.some((s) => selectedStates.includes(s))) {
      return false;
    }
    if (selectedAccounts.length > 0 && !selectedAccounts.includes(invoice.accountNumber)) {
      return false;
    }
    if (filters.startDate) {
      const invoiceDate = localDateToDate(invoice.invoiceLocalDate);
      if (invoiceDate < new Date(filters.startDate)) return false;
    }
    if (filters.endDate) {
      const invoiceDate = localDateToDate(invoice.invoiceLocalDate);
      if (invoiceDate > new Date(filters.endDate)) return false;
    }
    return true;
  });
};

export const useInvoiceList = ({
  hasPayments,
  excludeNucleus = false,
}: {
  hasPayments: boolean;
  excludeNucleus?: boolean;
}) => {
  const ctx = React.useContext(InvoiceListContext);
  const [localState, setLocalState] = React.useState<InvoiceListState>(initState());

  const state = ctx ? (hasPayments ? ctx.paidState : ctx.dueState) : localState;
  const setState = ctx ? (hasPayments ? ctx.setPaidState : ctx.setDueState) : setLocalState;

  const { user } = useRentalManagerUser();
  const stateRef = React.useRef(state);
  stateRef.current = state;
  const generationRef = React.useRef(0);

  const fetchPropertyData = () =>
    AdproductsUserBillingInvoiceApiHandler_getZuoraBillingFilterOptions()
      .then((result) => {
        return setState((newState) => ({
          ...newState,
          filters: {
            ...newState.filters,
            property: result.accountOptions.map((option) => ({
              label: option.accountName,
              isChecked: false,
              value: option.accountNumber,
            })),
            city: mapOptions(result.cities),
            state: mapOptions(result.states),
          },
        }));
      })
      .catch((error) => {
        logError({ error, errorType: 'BillingPortalLoadOptions' });
      });

  const fetchBillingData = () => {
    const fetchGeneration = generationRef.current;
    setState((lastState) => ({
      ...lastState,
      loadingState: LoadingConstants.LOADING,
    }));
    AdproductsUserBillingGetInvoicesApiHandler_scanInvoices({
      hasPayments,
      cursor: stateRef.current.cursor,
    })
      .then((result) => {
        if (generationRef.current !== fetchGeneration) return;
        return setState((lastState) => {
          const newInvoices = result.invoices.invoiceList ?? [];
          newInvoices.sort(invoiceSort);
          const existingIds = new Set(lastState.invoices.map((inv) => inv.invoiceId));
          const deduped = newInvoices.filter((inv) => !existingIds.has(inv.invoiceId));
          return {
            ...lastState,
            invoices: lastState.invoices.concat(deduped),
            cursor: result.lastPageKey,
            hasMoreData: result.hasMoreData,
            multiPaymentsEnabled: result.invoices.multiPaymentsEnabled,
            loadingState: LoadingConstants.SUCCESS,
          };
        });
      })
      .catch(() => {
        if (generationRef.current !== fetchGeneration) return;
        setState((lastState) => ({
          ...lastState,
          loadingState: LoadingConstants.ERROR,
        }));
      });
  };

  const setFilters = (newFilters: InvoiceListFilterConfig) => {
    emitSetFilterEvent({ user });
    setState((prev) => ({
      ...prev,
      filters: newFilters,
    }));
  };

  const refreshInvoices = () => {
    generationRef.current += 1;
    if (ctx?.clearAll) {
      ctx.clearAll();
    } else {
      setState(initState());
    }
  };

  const filteredInvoices = React.useMemo(
    () => applyClientFilters(state.invoices, state.filters, excludeNucleus),
    [state.invoices, state.filters, excludeNucleus],
  );

  return {
    invoices: filteredInvoices,
    hasMoreData: state.hasMoreData,
    loadingState: state.loadingState,
    errorMsg: state.errorMsg,
    filters: state.filters,
    multiPaymentsEnabled: state.multiPaymentsEnabled,
    fetchBillingData,
    fetchPropertyData,
    setFilters,
    refreshInvoices,
  };
};
